import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { PageContainer } from "@/components/pro/PageContainer";
import { PageHeader } from "@/components/pro/PageHeader";
import { Gift, Settings, CreditCard, CheckCircle, Clock, XCircle, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface GiftCardSettings {
  id?: string;
  is_enabled: boolean;
  predefined_amounts: number[];
  allow_custom_amount: boolean;
  min_custom_amount: number;
  max_custom_amount: number;
  validity_months: number;
}

interface GiftCard {
  id: string;
  code: string;
  amount: number;
  recipient_name: string;
  recipient_email: string;
  sender_name: string;
  sender_email: string;
  personal_message: string | null;
  status: string;
  is_redeemed: boolean;
  redeemed_amount: number;
  remaining_balance: number;
  expires_at: string;
  created_at: string;
}

export default function GiftCardManagement() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<GiftCardSettings>({
    is_enabled: false,
    predefined_amounts: [30, 50, 75, 100],
    allow_custom_amount: true,
    min_custom_amount: 20,
    max_custom_amount: 500,
    validity_months: 12,
  });
  const [giftCards, setGiftCards] = useState<GiftCard[]>([]);
  const [amountInput, setAmountInput] = useState("");

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate("/auth");
        return;
      }

      // Load settings
      const { data: settingsData } = await supabase
        .from("gift_card_settings")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();

      if (settingsData) {
        setSettings({
          id: settingsData.id,
          is_enabled: settingsData.is_enabled,
          predefined_amounts: Array.isArray(settingsData.predefined_amounts)
            ? settingsData.predefined_amounts as number[]
            : JSON.parse(settingsData.predefined_amounts as string),
          allow_custom_amount: settingsData.allow_custom_amount,
          min_custom_amount: settingsData.min_custom_amount,
          max_custom_amount: settingsData.max_custom_amount,
          validity_months: settingsData.validity_months,
        });
      }

      // Load gift cards
      const { data: cardsData, error: cardsError } = await supabase
        .from("gift_cards")
        .select("*")
        .order("created_at", { ascending: false });

      if (cardsError) throw cardsError;
      setGiftCards(cardsData || []);
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const payload = {
        user_id: user.id,
        is_enabled: settings.is_enabled,
        predefined_amounts: settings.predefined_amounts,
        allow_custom_amount: settings.allow_custom_amount,
        min_custom_amount: settings.min_custom_amount,
        max_custom_amount: settings.max_custom_amount,
        validity_months: settings.validity_months,
      };

      if (settings.id) {
        const { error } = await supabase
          .from("gift_card_settings")
          .update(payload)
          .eq("id", settings.id);
        if (error) throw error;
      } else {
        const { data, error } = await supabase
          .from("gift_card_settings")
          .insert([payload])
          .select()
          .single();
        if (error) throw error;
        setSettings({ ...settings, id: data.id });
      }

      toast.success("Paramètres sauvegardés");
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const addPredefinedAmount = () => {
    const amount = parseInt(amountInput);
    if (amount && amount > 0 && !settings.predefined_amounts.includes(amount)) {
      setSettings({
        ...settings,
        predefined_amounts: [...settings.predefined_amounts, amount].sort((a, b) => a - b),
      });
      setAmountInput("");
    }
  };

  const removePredefinedAmount = (amount: number) => {
    setSettings({
      ...settings,
      predefined_amounts: settings.predefined_amounts.filter((a) => a !== amount),
    });
  };

  const getStatusBadge = (card: GiftCard) => {
    if (card.status === "pending") {
      return <Badge variant="outline" className="text-orange-600 border-orange-300"><Clock className="w-3 h-3 mr-1" />En attente</Badge>;
    }
    if (card.is_redeemed) {
      return <Badge variant="outline" className="text-blue-600 border-blue-300"><CheckCircle className="w-3 h-3 mr-1" />Utilisée</Badge>;
    }
    if (new Date(card.expires_at) < new Date()) {
      return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Expirée</Badge>;
    }
    if (card.status === "paid") {
      return <Badge className="bg-primary"><CheckCircle className="w-3 h-3 mr-1" />Active</Badge>;
    }
    return <Badge variant="outline">{card.status}</Badge>;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const deleteGiftCard = async (cardId: string) => {
    try {
      const { error } = await supabase
        .from("gift_cards")
        .delete()
        .eq("id", cardId);

      if (error) throw error;

      setGiftCards(giftCards.filter((c) => c.id !== cardId));
      toast.success("Carte cadeau supprimée");
    } catch (error) {
      console.error("Error deleting gift card:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  const stats = {
    total: giftCards.length,
    active: giftCards.filter((c) => c.status === "paid" && !c.is_redeemed && new Date(c.expires_at) > new Date()).length,
    redeemed: giftCards.filter((c) => c.is_redeemed).length,
    totalValue: giftCards.filter((c) => c.status === "paid").reduce((sum, c) => sum + c.amount, 0),
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg">Chargement...</div>
      </div>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="Cartes Cadeaux"
        subtitle="Gérez vos cartes cadeaux et leurs paramètres"
        icon={<Gift className="w-6 h-6 sm:w-7 sm:h-7" />}
        iconColor="secondary"
      />

      <div className="space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-primary">{stats.total}</p>
              <p className="text-sm text-muted-foreground">Total vendues</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-primary">{stats.active}</p>
              <p className="text-sm text-muted-foreground">Actives</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-primary">{stats.redeemed}</p>
              <p className="text-sm text-muted-foreground">Utilisées</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-secondary">{stats.totalValue}€</p>
              <p className="text-sm text-muted-foreground">Valeur totale</p>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="cards" className="space-y-4">
          {/* Mobile-friendly tabs */}
          <div className="overflow-x-auto -mx-4 px-4 pb-2 sm:mx-0 sm:px-0">
            <TabsList className="inline-flex w-max sm:w-auto gap-1">
              <TabsTrigger value="cards" className="gap-1 sm:gap-2 px-3 sm:px-4 text-xs sm:text-sm whitespace-nowrap">
                <CreditCard className="w-3 h-3 sm:w-4 sm:h-4" />
                Cartes vendues
              </TabsTrigger>
              <TabsTrigger value="settings" className="gap-1 sm:gap-2 px-3 sm:px-4 text-xs sm:text-sm whitespace-nowrap">
                <Settings className="w-3 h-3 sm:w-4 sm:h-4" />
                Paramètres
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="cards">
            <Card>
              <CardHeader>
                <CardTitle>Historique des cartes cadeaux</CardTitle>
                <CardDescription>Liste de toutes les cartes cadeaux vendues</CardDescription>
              </CardHeader>
              <CardContent>
                {giftCards.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    Aucune carte cadeau vendue pour le moment
                  </p>
                ) : (
                  <>
                    {/* Mobile: Cartes empilées */}
                    <div className="block sm:hidden space-y-3">
                      {giftCards.map((card) => (
                        <div key={card.id} className="p-4 rounded-lg border bg-card space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="font-mono font-bold text-primary">{card.code}</span>
                            {getStatusBadge(card)}
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div>
                              <p className="text-muted-foreground text-xs">Montant</p>
                              <p className="font-semibold">{card.amount}€</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground text-xs">Solde</p>
                              <p className="font-semibold">{card.remaining_balance !== null ? `${card.remaining_balance}€` : "-"}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground text-xs">Destinataire</p>
                              <p className="font-medium truncate">{card.recipient_name}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground text-xs">Expiration</p>
                              <p>{formatDate(card.expires_at)}</p>
                            </div>
                          </div>
                          <div className="flex justify-end pt-2 border-t">
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive">
                                  <Trash2 className="w-4 h-4 mr-1" />
                                  Supprimer
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Supprimer la carte cadeau ?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Cette action est irréversible.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Annuler</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => deleteGiftCard(card.id)}>
                                    Supprimer
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Desktop: Tableau */}
                    <div className="hidden sm:block overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Code</TableHead>
                          <TableHead>Montant</TableHead>
                          <TableHead>Destinataire</TableHead>
                          <TableHead>Acheteur</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Solde</TableHead>
                          <TableHead>Expiration</TableHead>
                          <TableHead>Date d'achat</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {giftCards.map((card) => (
                          <TableRow key={card.id}>
                            <TableCell className="font-mono font-semibold">{card.code}</TableCell>
                            <TableCell className="font-semibold">{card.amount}€</TableCell>
                            <TableCell>
                              <div>
                                <p className="font-medium">{card.recipient_name}</p>
                                <p className="text-sm text-muted-foreground">{card.recipient_email}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div>
                                <p className="font-medium">{card.sender_name}</p>
                                <p className="text-sm text-muted-foreground">{card.sender_email}</p>
                              </div>
                            </TableCell>
                            <TableCell>{getStatusBadge(card)}</TableCell>
                            <TableCell>
                              {card.remaining_balance !== null ? `${card.remaining_balance}€` : "-"}
                            </TableCell>
                            <TableCell>{formatDate(card.expires_at)}</TableCell>
                            <TableCell>{formatDate(card.created_at)}</TableCell>
                            <TableCell className="text-right">
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive">
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Supprimer cette carte cadeau ?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Cette action est irréversible. La carte cadeau "{card.code}" d'une valeur de {card.amount}€ sera définitivement supprimée.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Annuler</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => deleteGiftCard(card.id)}
                                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                    >
                                      Supprimer
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="settings">
            <Card>
              <CardHeader>
                <CardTitle>Paramètres des cartes cadeaux</CardTitle>
                <CardDescription>Configurez les options d'affichage et de vente</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                  <div>
                    <Label className="text-base font-semibold">Activer les cartes cadeaux</Label>
                    <p className="text-sm text-muted-foreground">
                      Afficher la section cartes cadeaux sur votre boutique
                    </p>
                  </div>
                  <Switch
                    checked={settings.is_enabled}
                    onCheckedChange={(checked) => setSettings({ ...settings, is_enabled: checked })}
                  />
                </div>

                <div className="space-y-4">
                  <Label className="text-base font-semibold">Montants prédéfinis</Label>
                  <div className="flex flex-wrap gap-2">
                    {settings.predefined_amounts.map((amount) => (
                      <Badge
                        key={amount}
                        variant="secondary"
                        className="text-lg px-4 py-2 cursor-pointer hover:bg-destructive/20"
                        onClick={() => removePredefinedAmount(amount)}
                      >
                        {amount}€ ×
                      </Badge>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      placeholder="Nouveau montant"
                      value={amountInput}
                      onChange={(e) => setAmountInput(e.target.value)}
                      className="w-40"
                    />
                    <Button variant="outline" onClick={addPredefinedAmount}>
                      Ajouter
                    </Button>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label>Autoriser les montants personnalisés</Label>
                    <p className="text-sm text-muted-foreground">
                      Permet aux clients de choisir leur propre montant
                    </p>
                  </div>
                  <Switch
                    checked={settings.allow_custom_amount}
                    onCheckedChange={(checked) => setSettings({ ...settings, allow_custom_amount: checked })}
                  />
                </div>

                {settings.allow_custom_amount && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="minAmount">Montant minimum (€)</Label>
                      <Input
                        id="minAmount"
                        type="number"
                        value={settings.min_custom_amount}
                        onChange={(e) => setSettings({ ...settings, min_custom_amount: parseInt(e.target.value) || 20 })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="maxAmount">Montant maximum (€)</Label>
                      <Input
                        id="maxAmount"
                        type="number"
                        value={settings.max_custom_amount}
                        onChange={(e) => setSettings({ ...settings, max_custom_amount: parseInt(e.target.value) || 500 })}
                      />
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="validity">Durée de validité (mois)</Label>
                  <Input
                    id="validity"
                    type="number"
                    value={settings.validity_months}
                    onChange={(e) => setSettings({ ...settings, validity_months: parseInt(e.target.value) || 12 })}
                    className="w-40"
                  />
                </div>

                <Button onClick={saveSettings} disabled={saving} className="w-full">
                  {saving ? "Sauvegarde..." : "Sauvegarder les paramètres"}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </PageContainer>
  );
}
